import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

// Cache en memoria + localStorage por navegador, sobre la cache compartida
// en Firestore que mantiene la Cloud Function translateBatch (traducciones
// compartidas entre todos los usuarios, no por persona). Esto evita llamar
// a la función de nuevo por cada pantalla dentro de la misma sesión.
const memoryCache = new Map(); // `${lang}::${text}` -> texto traducido
const STORAGE_KEY = "gexvi_translation_cache_v1";

function loadLocalCache() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    Object.entries(parsed).forEach(([key, value]) => memoryCache.set(key, value));
  } catch {
    // localStorage puede no estar disponible (modo privado, etc.)
  }
}

function persistLocalCache() {
  try {
    const obj = Object.fromEntries(memoryCache.entries());
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    // ignorar
  }
}

loadLocalCache();

let translateBatchCallable = null;
const getCallable = () => {
  if (!translateBatchCallable) {
    translateBatchCallable = httpsCallable(functions, "translateBatch");
  }
  return translateBatchCallable;
};

// Traduce una lista de textos en español al idioma pedido. Devuelve el
// mismo texto original si targetLang es "es" o si algo falla (nunca deja
// la UI en blanco por un error de traducción).
export async function translateBatch(texts, targetLang) {
  if (!targetLang || targetLang === "es" || !texts.length) return texts;

  const results = new Array(texts.length);
  const missingIndexes = [];
  const missingTexts = [];

  texts.forEach((text, index) => {
    const cacheKey = `${targetLang}::${text}`;
    if (memoryCache.has(cacheKey)) {
      results[index] = memoryCache.get(cacheKey);
    } else {
      missingIndexes.push(index);
      missingTexts.push(text);
    }
  });

  if (missingTexts.length === 0) return results;

  try {
    const { data } = await getCallable()({ texts: missingTexts, targetLang });
    const translations = data?.translations || missingTexts;
    missingIndexes.forEach((resultIndex, i) => {
      const translated = translations[i] ?? missingTexts[i];
      results[resultIndex] = translated;
      memoryCache.set(`${targetLang}::${missingTexts[i]}`, translated);
    });
    persistLocalCache();
  } catch (error) {
    console.error("Error traduciendo:", error);
    missingIndexes.forEach((resultIndex, i) => {
      results[resultIndex] = missingTexts[i];
    });
  }

  return results;
}
